import React, { useState } from 'react'
import { Tooltip, TooltipProps, Box } from '@mui/material'

/**
 * Custom tooltip wrapper component that is accessible to screen readers and keyboard users.
 */

interface AccessibleTooltipProps extends Omit<TooltipProps, 'children'> {
  children: React.ReactElement
  title: React.ReactNode
  ariaLabel?: string
}

export const AccessibleTooltip: React.FC<AccessibleTooltipProps> = ({
  children,
  title,
  ariaLabel,
  ...tooltipProps
}) => {
  // get text content for tooltip
  const getTextContent = (node: React.ReactNode): string => {
    if (typeof node === 'string') return node
    if (typeof node === 'number') return String(node)
    if (React.isValidElement(node)) {
      const children = node.props.children
      if (typeof children === 'string') return children
      if (Array.isArray(children)) {
        return children.map(getTextContent).join(' ')
      }
      return getTextContent(children)
    }
    if (Array.isArray(node)) {
      return node.map(getTextContent).join(' ')
    }
    return ''
  }

  const textContent = ariaLabel || getTextContent(title)
  const [open, setOpen] = useState(false)

  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose()
    }
  }

  return (
    <Tooltip
      {...tooltipProps}
      title={title}
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      disableHoverListener={false}
      disableFocusListener={false}
      disableTouchListener={false}
      onKeyDown={handleKeyDown}
    >
      {React.cloneElement(children, {
        tabIndex: children.props.tabIndex ?? 0,
        'aria-label': children.props['aria-label'] ?? textContent,
        onFocus: (e: React.FocusEvent) => {
          handleOpen()
          if (children.props.onFocus) {
            children.props.onFocus(e)
          }
        },
        onBlur: (e: React.FocusEvent) => {
          handleClose()
          if (children.props.onBlur) {
            children.props.onBlur(e)
          }
        },
        onMouseEnter: (e: React.MouseEvent) => {
          handleOpen()
          if (children.props.onMouseEnter) {
            children.props.onMouseEnter(e)
          }
        },
        onMouseLeave: (e: React.MouseEvent) => {
          handleClose()
          if (children.props.onMouseLeave) {
            children.props.onMouseLeave(e)
          }
        },
      })}
    </Tooltip>
  )
}

interface AccessibleInfoIconProps {
  tooltip: string
  sx?: object
  size?: 'small' | 'medium' | 'large'
}

export const AccessibleInfoIcon: React.FC<AccessibleInfoIconProps> = ({
  tooltip,
  sx = {},
  size = 'small',
}) => {
  const [open, setOpen] = useState(false)

  const handleToggle = () => setOpen(!open)
  const handleClose = () => setOpen(false)

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handleToggle()
    } else if (event.key === 'Escape') {
      handleClose()
    }
  }

  return (
    <Tooltip
      title={tooltip}
      open={open}
      onClose={handleClose}
      disableHoverListener
      disableFocusListener
      disableTouchListener
    >
      <Box
        component="button"
        role="button"
        tabIndex={0}
        aria-label={tooltip}
        onFocus={() => setOpen(true)}
        onBlur={handleClose}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={handleClose}
        onKeyDown={handleKeyDown}
        sx={{
          border: 'none',
          background: 'transparent',
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          '&:focus': {
            outline: '2px solid #1976d2',
            outlineOffset: '2px',
          },
          ...sx,
        }}
      >
        <Box
          component="span"
          sx={{
            width: size === 'small' ? 16 : size === 'medium' ? 20 : 24,
            height: size === 'small' ? 16 : size === 'medium' ? 20 : 24,
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 0, 0, 0.54)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize:
              size === 'small' ? '12px' : size === 'medium' ? '14px' : '16px',
            fontWeight: 'bold',
          }}
        >
          i
        </Box>
      </Box>
    </Tooltip>
  )
}

export default AccessibleTooltip
